import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { authSchema, type AuthFormData } from "@/lib/authValidation";
import { AlertCircle, Lock, Mail } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

const Auth = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signIn, signUp, user } = useAuth();
  
  const [formData, setFormData] = useState<AuthFormData>({ email: "", password: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof AuthFormData, string>>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"login" | "signup">("login");

  // Redirect if already logged in
  if (user) {
    navigate("/admin/tracking");
    return null;
  }

  const handleSubmit = async (e: React.FormEvent, mode: "login" | "signup") => {
    e.preventDefault();
    setErrors({});
    setIsLoading(true);

    // Validate
    const validation = authSchema.safeParse(formData);
    if (!validation.success) {
      const formErrors: Partial<Record<keyof AuthFormData, string>> = {};
      validation.error.errors.forEach((error) => {
        const path = error.path[0] as keyof AuthFormData;
        formErrors[path] = error.message;
      });
      setErrors(formErrors);
      setIsLoading(false);
      return;
    }

    try {
      const { error } = mode === "login" 
        ? await signIn(validation.data.email, validation.data.password)
        : await signUp(validation.data.email, validation.data.password);

      if (error) {
        // Handle specific auth errors
        if (error.message.includes("Invalid login credentials")) {
          toast({
            title: "Login fehlgeschlagen",
            description: "E-Mail oder Passwort ist falsch.",
            variant: "destructive",
          });
        } else if (error.message.includes("User already registered")) {
          toast({
            title: "E-Mail bereits registriert",
            description: "Diese E-Mail-Adresse ist bereits registriert. Bitte melden Sie sich an.",
            variant: "destructive",
          });
          setActiveTab("login");
        } else {
          toast({
            title: "Fehler",
            description: error.message,
            variant: "destructive",
          });
        }
        setIsLoading(false);
        return;
      }

      // Success
      if (mode === "signup") {
        toast({
          title: "Erfolgreich registriert",
          description: "Sie können sich jetzt anmelden.",
        });
        setActiveTab("login");
        setFormData({ email: "", password: "" });
      } else {
        toast({
          title: "Erfolgreich angemeldet",
          description: "Willkommen zurück!",
        });
        navigate("/admin/tracking");
      }
    } catch (error) {
      toast({
        title: "Fehler",
        description: "Ein unerwarteter Fehler ist aufgetreten.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 shadow-glow">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-xl bg-gradient-primary flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary-foreground" />
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            EAP® Admin
          </h1>
          <p className="text-muted-foreground">
            Zugriff auf Tracking & Einstellungen
          </p>
        </div>

        <Tabs value="login">
          <TabsList className="grid w-full grid-cols-1 mb-6">
            <TabsTrigger value="login">Anmelden</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={(e) => handleSubmit(e, "login")} className="space-y-4">
              <div>
                <Label htmlFor="login-email">E-Mail</Label>
                <div className="relative mt-2">
                  <Mail className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="ihre@email.de"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`pl-10 ${errors.email ? 'border-destructive' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                {errors.email && (
                  <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="login-password">Passwort</Label>
                <div className="relative mt-2">
                  <Lock className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={`pl-10 ${errors.password ? 'border-destructive' : ''}`}
                    disabled={isLoading}
                  />
                </div>
                {errors.password && (
                  <p className="text-sm text-destructive mt-1 flex items-center gap-1">
                    <AlertCircle className="w-4 h-4" />
                    {errors.password}
                  </p>
                )}
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-primary-hover text-primary-foreground shadow-glow"
                disabled={isLoading}
              >
                {isLoading ? "Wird angemeldet..." : "Anmelden"}
              </Button>
            </form>
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-center">
          <Button
            variant="link"
            onClick={() => navigate("/")}
            className="text-muted-foreground hover:text-primary"
          >
            Zurück zur Homepage
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default Auth;
